import { getChallengeGroup } from '@selectors/challengeGroups';
import Form from '@components/form/Form';
import FormRow from 'lib/src/components/form/FormRow';
import TextInput from '@components/form/TextInput';
import Select from '@components/form/Select';
import DatePicker from '@components/form/DatePicker';
import Description from 'lib/src/components/typography/Description';
import Title from 'lib/src/components/typography/Title';
import { useHistory, useParams } from 'react-router';
import { useAppSelector } from '../../../redux/store';
import useEditChallenge from './hooks/useEditChallenge';
import MultiSelect from '@components/form/MultiSelect';
import config from '../../../config';
import FormField from 'lib/src/components/form/FormField';
import ContentBlock from '@components/layout/contentBlock/ContentBlock';
import React, { useState } from 'react';
import Breadcrumb from '@components/breadcrumb/Breadcrumb';
import DataCheck from 'lib/src/components/common/DataCheck';
import { isEmpty } from 'lib/src/utils/generic';
import TextArea from '@components/form/TextArea';
import {
    ChallengeDescriptionSectionRequest,
    DescriptionSectionTypes,
} from '../../../types/shared/Challenge';
import WYSIWYGInput from './WYSIWYGInput';
import AccordionInput from './AccordionInput';
import VideoInput from './VideoInput';
import ImageInput from './ImageInput';
import ActionButton from 'lib/src/components/button/ActionButton';
import { FILE_TYPES } from 'lib/src/constants/fileMimeTypes';
import ContentRow from '@components/layout/contentBlock/ContentRow';
import ContentItem from '@components/layout/contentBlock/ContentItem';
import FilePicker from '@components/filepicker/FilePicker';

const { API_URL, S3_URL } = config;
const { JPG, PNG } = FILE_TYPES;

const sectionsDropdown = [
    { label: 'WYSIWYG', value: 0 },
    { label: 'Accordion', value: 1 },
    { label: 'Video', value: 2 },
    { label: 'Image', value: 3 },
];

const EditChallenge = () => {
    const { id: challengeGroupID, challengeid } = useParams<{ id: string; challengeid: string }>();
    const history = useHistory();
    const group = useAppSelector(state => getChallengeGroup(state, +challengeGroupID));

    const {
        formState,
        handleChange,
        handleSubmit,
        isPosting,
        adminOptions,
        tagOptions,
        businessTagOptions,
        challenge,
        isFetching,
        fetchError,
        workshopAdminOptions,
        assessorOptions,
        pitchPresentationAdminOptions,
    } = useEditChallenge(+challengeid);
    const {
        name,
        challengeLeadID,
        partnerLogoS3Key,
        partnerSecondLogoS3Key,
        partnerThirdLogoS3Key,
        imageS3Key,
        startDate,
        endDate,
        tagIDs,
        businessTagIDs,
        gracePeriod,
        responseDate,
        background,
        descriptionSections,
        awardDescription,
        workshopDate,
        workshopAdminIDs,
        assessmentStartDate,
        assessorIDs,
        pitchPresentationDate,
        pitchPresentationAdminIDs,
    } = formState;

    const breadcrumbItems = [
        {
            name: 'Dashboard',
            link: '/',
        },
        {
            name: 'Challenge Groups',
            link: '/challenge-groups',
        },
        {
            name: group.name || '-',
            link: `/challenge-groups/${challengeGroupID}`,
        },
        {
            name: name || '-',
            link: `/challenge-groups/${challengeGroupID}/challenges/${challengeid}`,
        },
    ];

    const [sectionToAdd, setSectionToAdd] = useState<number | null>(null);
    const handleSetSectionToAdd = (name: string, value: number | null) => setSectionToAdd(value);

    const descriptionInputs = {
        [DescriptionSectionTypes.WYSIWYG]: WYSIWYGInput,
        [DescriptionSectionTypes.Accordion]: AccordionInput,
        [DescriptionSectionTypes.Video]: VideoInput,
        [DescriptionSectionTypes.Image]: ImageInput,
    };

    function handleAddSection(e: React.SyntheticEvent, type: number | null) {
        e.preventDefault();

        if (type === null) {
            return;
        }
        const currentMaxSort = descriptionSections.length
            ? descriptionSections.reduce((max, section) => Math.max(max, section.sort), 0)
            : 0;

        const newValue = [
            ...descriptionSections,
            {
                type,
                title: '',
                sort: currentMaxSort + 1,
                contents: [
                    {
                        id: 106,
                        sectionID: 104,
                        content: '',
                        title: '',
                        sort: 0,
                    },
                ],
            },
        ];
        handleChange('descriptionSections', newValue);
    }

    function handleDeleteSection(index: number) {
        const newValue = [
            ...descriptionSections.slice(0, index),
            ...descriptionSections.slice(index + 1, descriptionSections.length),
        ];

        handleChange(
            'descriptionSections',
            newValue.map((section, i) => ({ ...section, sort: i })),
        );
    }

    function handleUpdateSection(index: number, newData: ChallengeDescriptionSectionRequest) {
        const newValue = [
            ...descriptionSections.slice(0, index),
            newData,
            ...descriptionSections.slice(index + 1, descriptionSections.length),
        ];
        handleChange('descriptionSections', newValue);
    }

    function handleMoveSection(index: number, moveBy: number) {
        // moveBy can be -1 for move up, +1 for move down
        const rest = [
            ...descriptionSections.slice(0, index),
            ...descriptionSections.slice(index + 1, descriptionSections.length),
        ];
        handleChange(
            'descriptionSections',
            [
                ...rest.slice(0, index + moveBy),
                descriptionSections[index],
                ...rest.slice(index + moveBy, descriptionSections.length),
            ].map((section, i) => ({ ...section, sort: i })),
        );
    }

    return (
        <>
            <DataCheck dataExists={!isEmpty(challenge)} isFetching={isFetching} error={fetchError}>
                <Breadcrumb items={breadcrumbItems} title="Edit" />
                <Title>Edit Challenge</Title>
                <Description>
                    Edit the details below for
                    {' ' + group.name}
                </Description>
                <ContentBlock>
                    <Form
                        onSubmit={handleSubmit}
                        isPosting={isPosting}
                        onCancel={() => history.goBack()}
                        className="generic-form"
                    >
                        <ContentRow>
                            <ContentItem>
                                <TextArea
                                    name="name"
                                    value={name || ''}
                                    onChange={handleChange}
                                    label="Name"
                                    maxLength={255}
                                    required
                                />
                            </ContentItem>

                            <ContentItem>
                                <Select
                                    name="challengeLeadID"
                                    value={challengeLeadID}
                                    onChange={handleChange}
                                    label="Challenge Lead"
                                    options={adminOptions}
                                    required
                                    search
                                />
                            </ContentItem>
                        </ContentRow>
                        <ContentRow>
                            <ContentItem>
                                <TextArea
                                    name="background"
                                    value={background}
                                    onChange={handleChange}
                                    label="Challenge Summary"
                                    minRows={5}
                                />
                            </ContentItem>
                        </ContentRow>
                        <ContentRow>
                            <ContentItem>
                                <TextArea
                                    name="awardDescription"
                                    value={awardDescription}
                                    onChange={handleChange}
                                    label="Benefit"
                                    minRows={5}
                                />
                            </ContentItem>
                        </ContentRow>
                        <ContentRow>
                            <ContentItem>
                                <MultiSelect
                                    value={tagIDs}
                                    onChange={handleChange}
                                    name="tagIDs"
                                    label="Tags"
                                    options={tagOptions}
                                    search
                                />
                            </ContentItem>
                            <ContentItem>
                                <MultiSelect
                                    value={businessTagIDs}
                                    onChange={handleChange}
                                    name="businessTagIDs"
                                    label="Business Tags"
                                    options={businessTagOptions}
                                    search
                                />
                            </ContentItem>
                        </ContentRow>
                        <Title>Workshop Date</Title>
                        <ContentRow>
                            <ContentItem>
                                <DatePicker
                                    value={workshopDate}
                                    onChange={handleChange}
                                    name="workshopDate"
                                    label="Workshop Date"
                                    required
                                />
                            </ContentItem>
                            <ContentItem>
                                <MultiSelect
                                    value={workshopAdminIDs}
                                    onChange={handleChange}
                                    name="workshopAdminIDs"
                                    label="Workshop Admins"
                                    options={workshopAdminOptions}
                                    search
                                />
                            </ContentItem>
                            <ContentItem></ContentItem>
                        </ContentRow>
                        <Title>Live Challenge</Title>
                        <ContentRow>
                            <ContentItem>
                                <DatePicker
                                    value={startDate}
                                    onChange={handleChange}
                                    name="startDate"
                                    label="Challenge Start"
                                    required
                                />
                            </ContentItem>
                            <ContentItem>
                                <DatePicker
                                    value={endDate}
                                    onChange={handleChange}
                                    name="endDate"
                                    label="Challenge End"
                                    required
                                />
                            </ContentItem>
                            <ContentItem>
                                <TextInput
                                    type="number"
                                    name="gracePeriod"
                                    value={`${gracePeriod}`}
                                    onChange={handleChange}
                                    label="Grace Period"
                                    required
                                />
                            </ContentItem>
                        </ContentRow>
                        <Title>Assessment</Title>
                        <ContentRow>
                            <ContentItem>
                                <DatePicker
                                    value={assessmentStartDate}
                                    onChange={handleChange}
                                    name="assessmentStartDate"
                                    label="Assessment Start"
                                    required
                                />
                            </ContentItem>
                            <ContentItem>
                                <DatePicker
                                    value={responseDate}
                                    onChange={handleChange}
                                    name="responseDate"
                                    label="Response Date"
                                    required
                                />
                            </ContentItem>
                            <ContentItem>
                                <MultiSelect
                                    value={assessorIDs}
                                    onChange={handleChange}
                                    name="assessorIDs"
                                    label="Assessment Admins"
                                    options={assessorOptions}
                                    search
                                />
                            </ContentItem>
                        </ContentRow>
                        <Title>Pitch</Title>
                        <ContentRow>
                            <ContentItem>
                                <DatePicker
                                    value={pitchPresentationDate}
                                    onChange={handleChange}
                                    name="pitchPresentationDate"
                                    label="Pitch Presentation Date"
                                    required
                                />
                            </ContentItem>
                            <ContentItem>
                                <MultiSelect
                                    value={pitchPresentationAdminIDs}
                                    onChange={handleChange}
                                    name="pitchPresentationAdminIDs"
                                    label="Pitch Admins"
                                    options={pitchPresentationAdminOptions}
                                    search
                                />
                            </ContentItem>
                            <ContentItem></ContentItem>
                        </ContentRow>
                        <ContentRow>
                            <ContentItem className="file-picker-item">
                                <FormField label="Partner Logo" name="partnerLogoS3Key">
                                    <FilePicker
                                        apiURL={API_URL}
                                        storageURL={S3_URL}
                                        name="partnerLogoS3Key"
                                        value={partnerLogoS3Key}
                                        onChange={handleChange}
                                        maxFiles={1}
                                        acceptedTypes={`${JPG}, ${PNG}`}
                                    />
                                </FormField>
                            </ContentItem>
                            <ContentItem className="file-picker-item">
                                <FormField
                                    label="Partner Logo second"
                                    name="partnerSecondLogoS3Key"
                                >
                                    <FilePicker
                                        apiURL={API_URL}
                                        storageURL={S3_URL}
                                        name="partnerSecondLogoS3Key"
                                        value={partnerSecondLogoS3Key}
                                        onChange={handleChange}
                                        maxFiles={1}
                                        acceptedTypes={`${JPG}, ${PNG}`}
                                    />
                                </FormField>
                            </ContentItem>
                        </ContentRow>
                        <ContentRow>
                            <ContentItem className="file-picker-item">
                                <FormField label="Partner logo third" name="partnerThirdLogoS3Key">
                                    <FilePicker
                                        apiURL={API_URL}
                                        storageURL={S3_URL}
                                        name="partnerThirdLogoS3Key"
                                        value={partnerThirdLogoS3Key}
                                        onChange={handleChange}
                                        maxFiles={1}
                                        acceptedTypes={`${JPG}, ${PNG}`}
                                    />
                                </FormField>
                            </ContentItem>
                            <ContentItem className="file-picker-item">
                                <FormField label="Thumbnail" name="imageS3Key" required>
                                    <FilePicker
                                        apiURL={API_URL}
                                        storageURL={S3_URL}
                                        name="imageS3Key"
                                        value={imageS3Key}
                                        onChange={handleChange}
                                        maxFiles={1}
                                        acceptedTypes={`${JPG}, ${PNG}`}
                                    />
                                </FormField>
                            </ContentItem>
                        </ContentRow>

                        <Title>Description</Title>
                        <FormRow>
                            <div className="description-builder">
                                {Array.isArray(descriptionSections)
                                    ? [...descriptionSections]
                                          .sort((a, b) => a.sort - b.sort)
                                          .map((item, i, { length }) => {
                                              const SpecificComponent =
                                                  descriptionInputs[item.type];
                                              return (
                                                  <SpecificComponent
                                                      key={i}
                                                      index={i}
                                                      value={item}
                                                      onChange={handleUpdateSection}
                                                      handleDelete={handleDeleteSection}
                                                      handleMoveSection={handleMoveSection}
                                                      API_URL={API_URL}
                                                      S3_URL={S3_URL}
                                                      shouldShowSortButton={length > 1}
                                                      canMoveUp={i > 0}
                                                      canMoveDown={i < length - 1}
                                                  />
                                              );
                                          })
                                    : null}
                                <Select
                                    placeholder="Add section type..."
                                    name="sectionToAdd"
                                    options={sectionsDropdown}
                                    value={sectionToAdd}
                                    onChange={handleSetSectionToAdd}
                                />
                                <ActionButton
                                    type="button"
                                    onClick={e => handleAddSection(e, sectionToAdd)}
                                    disabled={sectionToAdd === null}
                                >
                                    Add section
                                </ActionButton>
                            </div>
                        </FormRow>
                    </Form>
                </ContentBlock>
            </DataCheck>
        </>
    );
};

export default EditChallenge;
